/**
 * Locations Download Section Component
 * 
 * Container component for all location-related imports (Stops, Meeting Points, POIs, Geofences).
 * Provides sub-navigation within the Locations tab.
 * Follows the same pattern as RoutesDownloadSection and FleetsDownloadSection.
 * 
 * This component was created as part of PRD-01: Locations Tab Reorganization.
 * It replaces the standalone "Stops" tab with a "Locations" tab that consolidates
 * all location types using sub-navigation.
 * 
 * Architecture:
 * - Uses Material-UI Tabs for sub-navigation (consistent with main drawer tabs)
 * - Manages active sub-section state
 * - Passes all required props to active sub-section
 * - Includes placeholder sections for future location types
 * 
 * Sub-sections:
 * - Stops (Fixed Route) - ✅ Implemented via StopsTransferSection (read-only; route-scoped edits in Routes tab)
 * - Meeting Points (On-Demand) - ✅ Implemented via MeetingPointsDownloadSection (PRD-03)
 * - Points of Interest (On-Demand) - ✅ Implemented via PointsOfInterestDownloadSection (PRD-03)
 * - Geofences (On-Demand) - ✅ Implemented via GeofencesDownloadSection
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { StopsTransferSection } from './StopsTransferSection';
import { MeetingPointsDownloadSection } from './MeetingPointsDownloadSection';
import { PointsOfInterestDownloadSection } from './PointsOfInterestDownloadSection';
import { GeofencesDownloadSection } from './GeofencesDownloadSection';
import { PlaceholderSection } from './PlaceholderSection';
import { type Environment } from './EnvironmentSelector';
import { type TransferMode } from './ModeToggle';
import { type UATNetwork } from '../../../services/fixedRouteUatClient';

interface LocationsDownloadSectionProps {
  jwtToken: string;
  environment: Environment;
  networks: UATNetwork[];
  isLoadingNetworks: boolean;
  networkError: string | null;
  selectedProductId: string;
  selectedNetworkId: number | null;
  onProductChange: (productId: string) => void;
  onLoadNetworks: () => void;
  serviceDate: string;
  onServiceDateChange: (date: string) => void;
  timezone: string;
  onTimezoneChange: (tz: string) => void;
  downloadAcknowledged: boolean;
  onDownloadAcknowledgedChange: (acknowledged: boolean) => void;
}

type LocationSubSection = 'stops' | 'meeting-points' | 'pois' | 'geofences';

export const LocationsDownloadSection: React.FC<LocationsDownloadSectionProps> = ({
  jwtToken,
  environment,
  networks,
  isLoadingNetworks,
  networkError,
  selectedProductId,
  selectedNetworkId,
  onProductChange,
  onLoadNetworks,
  serviceDate,
  onServiceDateChange,
  timezone,
  onTimezoneChange,
  downloadAcknowledged,
  onDownloadAcknowledgedChange,
}) => {
  const { t } = useTranslation('mapDownload');
  const [activeSubSection, setActiveSubSection] = useState<LocationSubSection>('stops');

  const handleSubSectionChange = (_event: React.SyntheticEvent, newValue: number) => {
    const subSections: LocationSubSection[] = ['stops', 'meeting-points', 'pois', 'geofences'];
    setActiveSubSection(subSections[newValue]);
  };

  const getSubSectionIndex = (subSection: LocationSubSection): number => {
    const subSections: LocationSubSection[] = ['stops', 'meeting-points', 'pois', 'geofences'];
    return subSections.indexOf(subSection);
  };

  // Common props to pass to all sub-sections
  // Note: StopsTransferSection doesn't use serviceDate/timezone props, but we keep them in interface for future sub-sections
  const commonProps = {
    jwtToken,
    environment,
    networks,
    isLoadingNetworks,
    networkError,
    selectedProductId,
    selectedNetworkId,
    onProductChange,
    onLoadNetworks,
    downloadAcknowledged,
    onDownloadAcknowledgedChange,
  };

  return (
    <Box>
      {/* Sub-navigation tabs */}
      <Tabs
        value={getSubSectionIndex(activeSubSection)}
        onChange={handleSubSectionChange}
        aria-label={t('locations.tabs.ariaLabel')}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        <Tab label={t('locations.tabs.stops')} />
        <Tab label={t('locations.tabs.meetingPoints')} />
        <Tab label={t('locations.tabs.pointsOfInterest')} />
        <Tab label={t('locations.tabs.geofences')} />
      </Tabs>

      {/* Sub-section content */}
      {activeSubSection === 'stops' && (
        <StopsTransferSection
          {...commonProps}
        />
      )}

      {activeSubSection === 'meeting-points' && (
        <MeetingPointsDownloadSection
          {...commonProps}
          serviceDate={serviceDate}
          onServiceDateChange={onServiceDateChange}
          timezone={timezone}
          onTimezoneChange={onTimezoneChange}
        />
      )}

      {activeSubSection === 'pois' && (
        <PointsOfInterestDownloadSection
          {...commonProps}
          serviceDate={serviceDate}
          onServiceDateChange={onServiceDateChange}
          timezone={timezone}
          onTimezoneChange={onTimezoneChange}
        />
      )}

      {activeSubSection === 'geofences' && (
        <GeofencesDownloadSection
          {...commonProps}
          serviceDate={serviceDate}
          onServiceDateChange={onServiceDateChange}
          timezone={timezone}
          onTimezoneChange={onTimezoneChange}
        />
      )}
    </Box>
  );
};

export default LocationsDownloadSection;

